import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';

export type TimelineActor = {
  type: 'USER' | 'SYSTEM';
  userId?: string;
  displayName: string;
};

export type TimelineItemPayload = {
  title: string;
  description?: string;
  metadata?: Record<string, unknown>;
  referenceType?: string;
  referenceId?: string;
};

export type TimelineItem = {
  id: string;
  type: string;
  createdAt: string;
  createdBy: TimelineActor;
  payload: TimelineItemPayload;
};

export type PaginatedTimeline = {
  items: TimelineItem[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
};

export type LeadNoteDto = {
  id: string;
  leadId: string;
  note: string;
  createdBy: string;
  isArchived?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type LeadDetailArea = 'platform' | 'tenant';

@Injectable({ providedIn: 'root' })
export class LeadNotesService {
  constructor(private readonly api: ApiService) {}

  getTimeline(
    area: LeadDetailArea,
    leadId: string,
    page = 1,
    limit = 20,
    includeArchivedNotes = false,
  ) {
    const prefix = area === 'tenant' ? 'tenant/leads' : 'platform/leads';
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (includeArchivedNotes) {
      query.set('includeArchivedNotes', 'true');
    }
    return this.api
      .get<ApiEnvelope<PaginatedTimeline>>(`${prefix}/${leadId}/timeline?${query.toString()}`)
      .pipe(map((r) => r.data));
  }

  createNote(area: LeadDetailArea, leadId: string, note: string) {
    const prefix = area === 'tenant' ? 'tenant/leads' : 'platform/leads';
    return this.api
      .post<ApiEnvelope<LeadNoteDto>>(`${prefix}/${leadId}/notes`, { note })
      .pipe(map((r) => r.data));
  }

  updateNote(area: LeadDetailArea, leadId: string, noteId: string, note: string) {
    const prefix = area === 'tenant' ? 'tenant/leads' : 'platform/leads';
    return this.api
      .patch<ApiEnvelope<LeadNoteDto>>(`${prefix}/${leadId}/notes/${noteId}`, { note })
      .pipe(map((r) => r.data));
  }

  deleteNote(area: LeadDetailArea, leadId: string, noteId: string) {
    const prefix = area === 'tenant' ? 'tenant/leads' : 'platform/leads';
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`${prefix}/${leadId}/notes/${noteId}`)
      .pipe(map((r) => r.data));
  }

  restoreNote(area: LeadDetailArea, leadId: string, noteId: string) {
    const prefix = area === 'tenant' ? 'tenant/leads' : 'platform/leads';
    return this.api
      .post<ApiEnvelope<LeadNoteDto>>(`${prefix}/${leadId}/notes/${noteId}/restore`, {})
      .pipe(map((r) => r.data));
  }
}
